// components/SmartDuplicateDetector.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { Alert, Tag, Button, Modal, Descriptions } from 'antd';
import { WarningOutlined, EyeOutlined } from '@ant-design/icons';
//import AssetService from '../services/AssetService';
import AssetService from "../../services/AssetService";
import debounce from 'lodash/debounce';

const SmartDuplicateDetector = ({ 
    formData, 
    assetCatId,
    onViewAsset 
}) => {
    const [potentialDuplicates, setPotentialDuplicates] = useState([]);
    const [loading, setLoading] = useState(false);

    // Levenshtein distance for fuzzy matching
    const levenshteinDistance = (str1, str2) => {
        const matrix = [];
        
        for (let i = 0; i <= str2.length; i++) {
            matrix[i] = [i];
        }
        
        for (let j = 0; j <= str1.length; j++) {
            matrix[0][j] = j;
        }
        
        for (let i = 1; i <= str2.length; i++) {
            for (let j = 1; j <= str1.length; j++) {
                if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
                    matrix[i][j] = matrix[i - 1][j - 1];
                } else {
                    matrix[i][j] = Math.min(
                        matrix[i - 1][j - 1] + 1,
                        matrix[i][j - 1] + 1,
                        matrix[i - 1][j] + 1
                    );
                }
            }
        }
        
        return matrix[str2.length][str1.length];
    };

    const calculateSimilarity = (str1, str2) => {
        if (!str1 || !str2) return 0;
        
        str1 = str1.toLowerCase().trim();
        str2 = str2.toLowerCase().trim();
        
        if (str1 === str2) return 1;
        
        const distance = levenshteinDistance(str1, str2);
        const maxLength = Math.max(str1.length, str2.length);
        return 1 - (distance / maxLength);
    };

    const checkForDuplicates = useCallback(async () => {
        if (!formData.serial_no && !formData.item_id) {
            setPotentialDuplicates([]);
            return;
        }

        try {
            setLoading(true);
            
            // Search for similar assets
            const response = await AssetService.lists({
                asset_cat_id: assetCatId,
                client_id: formData.client_id,
                ps: 100
            });
            
            const existingAssets = response.data.result.data || [];
            const duplicates = [];
            
            existingAssets.forEach(asset => {
                // Skip if same asset (editing)
                if (formData.id && asset.id === formData.id) return;
                
                let similarity = 0;
                let matchReasons = [];
                
                // Exact matches
                if (formData.serial_no && asset.serial_no) {
                    const serialMatch = calculateSimilarity(
                        formData.serial_no, 
                        asset.serial_no
                    );
                    
                    if (serialMatch > 0.85) {
                        similarity = Math.max(similarity, serialMatch);
                        matchReasons.push(
                            `Serial/Plate: ${(serialMatch * 100).toFixed(0)}% match`
                        );
                    }
                }
                
                // Item match
                if (formData.item_id && asset.item_id === formData.item_id) {
                    similarity = Math.max(similarity, 0.5);
                    matchReasons.push('Same asset type');
                    
                    // Location match
                    if (formData.station_point_ids && asset.station_point_ids) {
                        const commonLocations = formData.station_point_ids.filter(
                            id => asset.station_point_ids.includes(id)
                        );
                        
                        if (commonLocations.length > 0) {
                            similarity = Math.max(similarity, 0.7);
                            matchReasons.push('Same location');
                        }
                    }
                }
                
                if (similarity >= 0.85) {
                    duplicates.push({
                        ...asset,
                        similarity,
                        matchReasons
                    });
                }
            });
            
            // Sort by similarity
            duplicates.sort((a, b) => b.similarity - a.similarity);
            setPotentialDuplicates(duplicates.slice(0, 5));
            
        } catch (error) {
            console.error('Duplicate detection error:', error);
        } finally {
            setLoading(false);
        }
    }, [formData, assetCatId]);

    const debouncedCheck = useCallback(
        debounce(checkForDuplicates, 800),
        [checkForDuplicates]
    );

    useEffect(() => {
        debouncedCheck();
        return () => debouncedCheck.cancel();
    }, [formData.serial_no, formData.item_id, formData.client_id, debouncedCheck]);

    const viewAssetDetails = (asset) => {
        Modal.info({
            title: 'Potential Duplicate Asset Details',
            width: 600,
            content: (
                <Descriptions bordered size="small" column={1}>
                    <Descriptions.Item label="Asset Name">{asset.item}</Descriptions.Item>
                    <Descriptions.Item label="Identifier">{asset.serial_no}</Descriptions.Item>
                    <Descriptions.Item label="Facility">{asset.client}</Descriptions.Item>
                    <Descriptions.Item label="Location">
                        {asset.station_points?.map(sp => sp.station_point).join(', ')}
                    </Descriptions.Item>
                    <Descriptions.Item label="Status">{asset.functional_status_name}</Descriptions.Item>
                    <Descriptions.Item label="Quantity">{asset.stock}</Descriptions.Item>
                    <Descriptions.Item label="Cost">E{asset.cost}</Descriptions.Item>
                    <Descriptions.Item label="Created">{asset.created}</Descriptions.Item>
                </Descriptions>
            ),
            onOk: () => onViewAsset && onViewAsset(asset.id)
        });
    };

    if (potentialDuplicates.length === 0) return null;

    return (
        <Alert
            message={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>
                        <WarningOutlined style={{ marginRight: 8 }} />
                        <strong>Potential Duplicates Detected ({potentialDuplicates.length})</strong>
                    </span>
                </div>
            }
            description={
                <div>
                    <p style={{ marginBottom: 12 }}>
                        Found similar assets that may already exist in the system:
                    </p>
                    {potentialDuplicates.map((dup, i) => (
                        <div key={i} style={{ 
                            marginBottom: 12,
                            padding: 10,
                            background: '#fff',
                            border: '1px solid #ffa940',
                            borderRadius: 4
                        }}>
                            <div style={{ 
                                display: 'flex', 
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 8
                            }}>
                                <div>
                                    <strong>{dup.item}</strong>
                                    {dup.serial_no && (
                                        <span style={{ marginLeft: 8, color: '#666' }}>
                                            ({dup.serial_no})
                                        </span>
                                    )}
                                </div>
                                <Tag color="orange">
                                    {(dup.similarity * 100).toFixed(0)}% Match
                                </Tag>
                            </div>
                            
                            <div style={{ fontSize: 12, color: '#666', marginBottom: 8 }}>
                                <strong>Facility:</strong> {dup.client}
                                <br />
                                <strong>Location:</strong> {dup.station_points?.map(sp => sp.station_point).join(', ')}
                            </div>
                            
                            <div style={{ marginBottom: 8 }}>
                                {dup.matchReasons.map((reason, j) => (
                                    <Tag key={j} style={{ marginRight: 4, marginBottom: 4 }}>
                                        {reason}
                                    </Tag>
                                ))}
                            </div>
                            
                            <Button 
                                size="small" 
                                icon={<EyeOutlined />}
                                onClick={() => viewAssetDetails(dup)}
                            >
                                View Details
                            </Button>
                        </div>
                    ))}
                    
                    <p style={{ 
                        marginTop: 12, 
                        fontSize: 12, 
                        color: '#666',
                        fontStyle: 'italic'
                    }}>
                        💡 Review these assets before creating a new one to avoid duplicates.
                    </p>
                </div>
            }
            type="warning"
            showIcon
            closable
            style={{ marginBottom: 16 }}
        />
    );
};

export default SmartDuplicateDetector;